import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { forumAPI } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Award, ArrowRight, Store, Users, MessagesSquare, Youtube, Instagram,
  Twitter, Facebook, Newspaper, Eye, Heart, ExternalLink, Flame,
} from "lucide-react";

function money(amount, currency = "USD") {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount || 0);
  } catch { return `$${amount}`; }
}

function sourceMeta(type) {
  switch (type) {
    case "youtube": return { icon: Youtube, color: "#FF0000", label: "YouTube" };
    case "instagram": return { icon: Instagram, color: "#E4405F", label: "Instagram" };
    case "twitter": return { icon: Twitter, color: "#000000", label: "X" };
    case "facebook": return { icon: Facebook, color: "#1877F2", label: "Facebook" };
    default: return { icon: Newspaper, color: "hsl(2 100% 55%)", label: "News" };
  }
}

function Shell({ kicker, children, testId }) {
  return (
    <Card className="overflow-hidden border-border bg-card" data-testid={testId}>
      {kicker && (
        <div className="flex items-center gap-2 border-b border-border/60 bg-secondary/40 px-3 py-2">
          <Flame className="h-3.5 w-3.5 text-primary" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {kicker}
          </span>
        </div>
      )}
      {children}
    </Card>
  );
}

function SponsorshipCtaCard({ data }) {
  return (
    <Shell kicker="Spotlight" testId="forum-spotlight-sponsorship">
      <div className="p-4">
        <Award className="mb-2 h-6 w-6 text-primary" />
        <h3 className="font-display text-base font-bold uppercase leading-tight">{data.title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{data.body}</p>
        <Link to={data.cta_url}>
          <Button size="sm" className="mt-3 w-full bg-primary text-primary-foreground hover:bg-primary/90">
            {data.cta_label} <ArrowRight className="ml-2 h-3.5 w-3.5" />
          </Button>
        </Link>
      </div>
    </Shell>
  );
}

function CustomCtaCard({ data }) {
  if (!data.title && !data.body && !data.image_url) return null;
  const inner = (
    <div className="p-4">
      {data.image_url && (
        <img
          src={data.image_url}
          alt=""
          className="mb-3 h-28 w-full rounded-md object-cover"
        />
      )}
      {data.title && (
        <h3 className="font-display text-base font-bold uppercase leading-tight">{data.title}</h3>
      )}
      {data.body && <p className="mt-1 text-sm text-muted-foreground">{data.body}</p>}
      {data.cta_label && data.cta_url && (
        <Button size="sm" className="mt-3 w-full bg-primary text-primary-foreground hover:bg-primary/90">
          {data.cta_label} <ArrowRight className="ml-2 h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
  const isExternal = data.cta_url && /^https?:/i.test(data.cta_url);
  return (
    <Shell kicker="Spotlight" testId="forum-spotlight-custom">
      {data.cta_url ? (
        isExternal ? (
          <a href={data.cta_url} target="_blank" rel="noopener noreferrer">{inner}</a>
        ) : (
          <Link to={data.cta_url}>{inner}</Link>
        )
      ) : inner}
    </Shell>
  );
}

function HotMarketplaceCard({ data }) {
  return (
    <Shell kicker="Hot in marketplace" testId="forum-spotlight-marketplace">
      <Link to={`/marketplace/${data.listing_id}`} className="block">
        {data.image_url && (
          <img
            src={data.image_url}
            alt=""
            className="h-32 w-full object-cover"
          />
        )}
        <div className="p-3">
          <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Store className="h-3 w-3" /> Marketplace
          </div>
          <p className="line-clamp-2 text-sm font-semibold">{data.title}</p>
          <div className="mt-2 flex items-center justify-between">
            <p className="font-display text-lg font-bold text-primary">{money(data.price, data.currency)}</p>
            {data.condition && <span className="text-[10px] uppercase text-muted-foreground">{data.condition}</span>}
          </div>
        </div>
      </Link>
    </Shell>
  );
}

function HotSocialCard({ data }) {
  return (
    <Shell kicker="Trending in social" testId="forum-spotlight-social">
      <Link to="/social" className="block">
        {data.image_url && (
          <img src={data.image_url} alt="" className="h-32 w-full object-cover" />
        )}
        <div className="p-3">
          <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Users className="h-3 w-3" /> Social
          </div>
          <p className="text-xs font-semibold">{data.author_username}</p>
          {data.content && <p className="mt-1 line-clamp-3 text-sm text-foreground/90">{data.content}</p>}
          <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {data.likes_count || 0}</span>
            <span className="flex items-center gap-1"><MessagesSquare className="h-3 w-3" /> {data.comments_count || 0}</span>
          </div>
        </div>
      </Link>
    </Shell>
  );
}

function HotForumCard({ data }) {
  return (
    <Shell kicker="Hot discussion" testId="forum-spotlight-forum">
      <Link to={`/forum/${data.post_id}`} className="block p-3">
        <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          <MessagesSquare className="h-3 w-3" /> {(data.category || "general").replace("_", " ")}
        </div>
        <p className="line-clamp-2 text-sm font-semibold leading-snug">{data.title}</p>
        <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
          <span>by {data.author_username}</span>
          <span className="flex items-center gap-1"><MessagesSquare className="h-3 w-3" /> {data.reply_count}</span>
          <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {data.views}</span>
        </div>
      </Link>
    </Shell>
  );
}

function HotCuratedCard({ data }) {
  const { icon: Icon, color, label } = sourceMeta(data.content_type);
  const open = () => data.content_url && window.open(data.content_url, "_blank", "noopener,noreferrer");
  return (
    <Shell kicker="From the feed" testId="forum-spotlight-curated">
      <button onClick={open} type="button" className="block w-full text-left">
        {data.thumbnail_url ? (
          <img src={data.thumbnail_url} alt="" className="h-32 w-full object-cover" />
        ) : (
          <div className="flex h-32 w-full items-center justify-center" style={{ background: `${color}20` }}>
            <Icon className="h-10 w-10" style={{ color }} />
          </div>
        )}
        <div className="p-3">
          <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider" style={{ color }}>
            <Icon className="h-3 w-3" /> {label}
          </div>
          <p className="line-clamp-2 text-sm font-semibold">{data.title}</p>
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span className="truncate">{data.source_name}</span>
            <span className="flex items-center gap-1">Open <ExternalLink className="h-3 w-3" /></span>
          </div>
        </div>
      </button>
    </Shell>
  );
}

function SlotRenderer({ slot }) {
  switch (slot.type) {
    case "sponsorship_cta": return <SponsorshipCtaCard data={slot.data} />;
    case "custom_cta": return <CustomCtaCard data={slot.data} />;
    case "hot_marketplace": return <HotMarketplaceCard data={slot.data} />;
    case "hot_social": return <HotSocialCard data={slot.data} />;
    case "hot_forum": return <HotForumCard data={slot.data} />;
    case "hot_curated": return <HotCuratedCard data={slot.data} />;
    default: return null;
  }
}

export default function ForumSpotlightRail() {
  const [slots, setSlots] = useState(null);

  useEffect(() => {
    forumAPI.getSpotlight()
      .then((r) => setSlots(r.data?.slots || []))
      .catch(() => setSlots([]));
  }, []);

  if (slots === null) {
    return (
      <aside className="space-y-4" data-testid="forum-spotlight-loading">
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-40 rounded-lg" />
      </aside>
    );
  }
  if (!slots.length) return null;

  return (
    <aside className="space-y-4 lg:sticky lg:top-24" data-testid="forum-spotlight-rail">
      {slots.map((s) => <SlotRenderer key={s.id} slot={s} />)}
    </aside>
  );
}
